import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, formatApiError, applyMeta, agencyLogoUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, Send, CheckCircle2, Lock, FileText } from "lucide-react";
import { FormFieldInput, FIELD_META, isFieldEmpty, visibleFields } from "@/components/FormFieldKit";

export default function PublicForm() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true });
  const [values, setValues] = useState({});
  const [busy, setBusy] = useState(false);
  const [uploads, setUploads] = useState(0);
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.get(`/public/forms/${token}`).then(({ data }) => {
      setState({ loading: false, ...data });
      if (data.agency_name) applyMeta({ meta_title: `${data.title} · ${data.agency_name}` });
    }).catch((e) => setState({ loading: false, notfound: true }));
  }, [token]);

  const uploader = async (file) => {
    setUploads((n) => n + 1);
    try { const fd = new FormData(); fd.append("file", file); const { data } = await api.post(`/public/forms/${token}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } }); return data; }
    finally { setUploads((n) => Math.max(0, n - 1)); }
  };
  const setVal = (id, v) => setValues((cur) => ({ ...cur, [id]: v }));

  const submit = async () => {
    const shown = visibleFields(state.fields || [], values);
    for (const fld of shown) {
      if (fld.required && FIELD_META[fld.type]?.input !== false) {
        if (isFieldEmpty(fld, values[fld.id])) { toast.error(`Please complete: ${fld.label}`); return; }
      }
    }
    setBusy(true);
    const nameField = shown.find((f) => f.type === "name");
    const emailField = shown.find((f) => f.type === "email");
    const submitter_name = nameField ? (values[nameField.id] || "") : "";
    const submitter_email = emailField ? (values[emailField.id] || "") : "";
    const data = {};
    shown.forEach((f) => { if (values[f.id] !== undefined) data[f.id] = values[f.id]; });
    try { await api.post(`/public/forms/${token}/submit`, { data, submitter_name, submitter_email }); setDone(true); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  if (state.loading) return <div className="min-h-screen flex items-center justify-center bg-[#F6F6FD]"><Loader2 className="h-6 w-6 animate-spin text-[#c4c5cc]" /></div>;

  if (state.notfound) return (
    <div className="min-h-screen flex items-center justify-center bg-[#F6F6FD] px-4">
      <div className="text-center"><FileText className="h-10 w-10 text-[#d3d4d8] mx-auto mb-3" /><p className="text-[#595D62]">This form isn't available.</p></div>
    </div>
  );

  if (state.requires_login) return (
    <div className="min-h-screen flex items-center justify-center bg-[#F6F6FD] px-4">
      <div className="max-w-sm w-full rounded-3xl border border-[#EEEFEF] bg-white p-8 text-center shadow-sm">
        <div className="h-12 w-12 rounded-2xl bg-[#EDE7FF] text-[#723FFF] flex items-center justify-center mx-auto mb-4"><Lock className="h-6 w-6" /></div>
        <h1 className="font-display text-xl font-semibold text-[#06060C]">{state.title}</h1>
        <p className="text-sm text-[#9FA1A4] mt-2">This is a private form. Please sign in to your workspace to complete it.</p>
        {state.slug && <Button data-testid="public-form-login" onClick={() => { window.location.href = state.form_id ? `/${state.slug}/forms/${state.form_id}` : `/${state.slug}`; }} className="mt-5 bg-[#06060C] text-white hover:bg-[#1c1c24] rounded-full">Sign in to continue</Button>}
      </div>
    </div>
  );

  if (done) return (
    <div className="min-h-screen flex items-center justify-center bg-[#F6F6FD] px-4">
      <div className="max-w-sm w-full rounded-3xl border border-[#EEEFEF] bg-white p-8 text-center shadow-sm" data-testid="public-form-done">
        <div className="h-12 w-12 rounded-2xl bg-[#1f9d55]/10 text-[#1f9d55] flex items-center justify-center mx-auto mb-4"><CheckCircle2 className="h-6 w-6" /></div>
        <h1 className="font-display text-xl font-semibold text-[#06060C]">Thank you!</h1>
        <p className="text-sm text-[#9FA1A4] mt-2">Your response to “{state.title}” has been submitted.</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F6F6FD] py-10 px-4">
      <div className="max-w-xl mx-auto" data-testid="public-form">
        {(() => { const logo = agencyLogoUrl({ agency_logo_path: state.agency_logo_path });
          return logo
            ? <img data-testid="public-form-logo" src={logo} alt={state.agency_name || "Logo"} className="h-10 w-auto object-contain mx-auto mb-5" />
            : (state.agency_name && <p className="text-center font-mono-data text-[11px] uppercase tracking-wider text-[#9FA1A4] mb-4">{state.agency_name}</p>);
        })()}
        <div className="rounded-3xl border border-[#EEEFEF] bg-white p-6 md:p-8 shadow-sm">
          <h1 className="font-display text-2xl font-semibold text-[#06060C]">{state.title}</h1>
          {state.description && <p className="text-sm text-[#9FA1A4] mt-1.5">{state.description}</p>}
          <div className="mt-6 space-y-4">
            {visibleFields(state.fields || [], values).map((fld) => (
              <FormFieldInput key={fld.id} field={fld} value={values[fld.id]} onChange={(v) => setVal(fld.id, v)} uploader={uploader} />
            ))}
          </div>
          <Button data-testid="public-form-submit" onClick={submit} disabled={busy || uploads > 0} className="mt-6 w-full bg-[#06060C] text-white hover:bg-[#1c1c24] rounded-full h-11">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : uploads > 0 ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Uploading files…</> : <><Send className="h-4 w-4 mr-1.5" /> Submit</>}</Button>
        </div>
        <p className="text-center text-[11px] text-[#c4c5cc] mt-4">Powered by Mission Control</p>
      </div>
    </div>
  );
}
